import React, { useContext, useEffect, useState, useMemo } from "react";
import { ThemeContext } from "../theme";
import { Search, ChevronDown, ChevronUp, MessageCircle } from "lucide-react";

const FAQ_DATA = [
    {
        category: "Gameplay",
        questions: [
            { q: "What is I Call On?", a: "I Call On is a fast-paced multiplayer word game where players respond to categories using words that start with a randomly selected letter. Creativity, speed, and originality earn points." },
            { q: "How many players can join a game?", a: "Games can include 2-10 players, making it perfect for small friend groups or larger competitive sessions." },
            { q: "How does scoring work?", a: "Points are awarded based on valid answers, uniqueness, and completion speed. Repeated or invalid answers may score lower or be rejected." },
            { q: "Can I skip a round?", a: "If time runs out, unanswered categories are skipped automatically. Skipping may affect your score for that round." },
        ],
    },
    {
        category: "Accounts",
        questions: [
            { q: "Do I need an account to play?", a: "You require an account to ensure fair play, track scores, and enable multiplayer games." },
            { q: "Can I change my username?", a: "Usernames can be updated depending on availability and moderation rules." },
        ],
    },
];

function Faqs() {
    const { colors } = useContext(ThemeContext)!;
    const [visible, setVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedIndex, setExpandedIndex] = useState<string | null>(null);

    useEffect(() => setVisible(true), []);

    // Search Logic: Filters categories and questions based on input
    const filteredFaqs = useMemo(() => {
        return FAQ_DATA.map(section => ({
            ...section,
            questions: section.questions.filter(
                item => item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.a.toLowerCase().includes(searchQuery.toLowerCase())
            )
        })).filter(section => section.questions.length > 0);
    }, [searchQuery]);

    const toggleAccordion = (id: string) => {
        setExpandedIndex(expandedIndex === id ? null : id);
    };

    return (
        <div className={`min-h-screen ${colors.bgSecondaryColor} transition-colors duration-300`}>
            <div className="container mx-auto px-6 py-4 md:py-20 max-w-3xl">
                <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>

                    {/* Header Section */}
                    <div className="text-center mb-12">
                        <h1 className={`text-4xl md:text-5xl font-black ${colors.textColor} mb-4`}>
                            How can we help?
                        </h1>
                        <p className={`${colors.textColor} opacity-70 text-lg`}>
                            Search our knowledge base or browse categories below.
                        </p>
                    </div>

                    {/* Modern Search Bar */}
                    <div className="relative mb-12">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 opacity-40" size={20} />
                        <input
                            type="text"
                            placeholder="Search for questions (e.g. 'scoring', 'account')..."
                            className={`w-full p-4 pl-12 rounded-2xl border ${colors.borderColor} ${colors.cardBg} ${colors.textColor} focus:ring-2 focus:ring-blue-500 outline-none transition-all`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {/* FAQ List */}
                    <div className="space-y-8">
                        {filteredFaqs.length > 0 ? (
                            filteredFaqs.map((section, sIdx) => (
                                <div key={section.category}>
                                    <h2 className={`text-xl font-bold ${colors.textColor} mb-4 px-2`}>
                                        {section.category}
                                    </h2>
                                    <div className={`rounded-2xl border ${colors.borderColor} overflow-hidden`}>
                                        {section.questions.map((item, qIdx) => {
                                            const id = `${sIdx}-${qIdx}`;
                                            const isOpen = expandedIndex === id;
                                            return (
                                                <div key={id} className={`border-b last:border-b-0 ${colors.borderColor}`}>
                                                    <button
                                                        onClick={() => toggleAccordion(id)}
                                                        className={`w-full flex items-center justify-between p-5 text-left hover:bg-black/5 transition-colors`}
                                                    >
                                                        <span className={`font-semibold ${colors.textColor}`}>{item.q}</span>
                                                        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                    </button>
                                                    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                                                        <p className={`p-5 pt-0 ${colors.textColor} opacity-80 leading-relaxed`}>
                                                            {item.a}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-10">
                                <p className={`${colors.textColor} opacity-60`}>No results found for "{searchQuery}"</p>
                            </div>
                        )}
                    </div>

                    {/* Modern Support CTA */}
                    <div className={`mt-16 p-8 rounded-3xl border ${colors.borderColor} bg-gradient-to-br from-blue-500/10 to-purple-500/10 text-center`}>
                        <div className={`${colors.primaryColor} w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4`}>
                            <MessageCircle className="text-white" />
                        </div>
                        <h3 className={`text-xl font-bold ${colors.textColor} mb-2`}>Still stuck?</h3>
                        <p className={`${colors.textColor} opacity-70 mb-6`}>
                            Our team usually responds within 24 hours.
                        </p>
                        <button className={`${colors.primaryColor} hover:${colors.primaryColor}/80 text-white px-8 py-3 rounded-full font-bold transition-transform active:scale-95`}>
                            Contact Support
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default Faqs;